// Copyright 2025, University of Maryland, All Rights Reserved

import React from 'react'
import { cn, Badge, MarkdownViewer } from '@/components/ui'
import { CollapsibleSection } from './CollapsibleSection'

export interface CollapsibleKBPanelProps {
  /** Label for the toggle (default: "KB Reference") */
  label?: string
  /** KB item number (e.g., "001") */
  kbItemId?: string
  /** KB item risk score */
  kbRiskScore?: number | null
  /** Markdown content to display */
  content: string
  /** Initial open state */
  defaultOpen?: boolean
  /** Additional className for the container */
  className?: string
  /** Additional className for the content panel */
  contentClassName?: string
}

/**
 * Collapsible panel for displaying KB (Knowledge Base) content with markdown rendering.
 * Shows KB item number and risk score in header when expanded.
 */
export const CollapsibleKBPanel: React.FC<CollapsibleKBPanelProps> = ({
  label = 'KB Reference',
  kbItemId,
  kbRiskScore,
  content,
  defaultOpen = false,
  className,
  contentClassName,
}) => {
  if (!content) return null

  return (
    <CollapsibleSection
      label={label}
      defaultOpen={defaultOpen}
      className={className}
    >
      <div
        className={cn(
          'rounded-lg border border-border/50 bg-muted/20',
          contentClassName
        )}
      >
        {/* KB header with number and risk badges */}
        {(kbItemId || typeof kbRiskScore === 'number') && (
          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-border/30">
            {kbItemId && (
              <Badge variant="secondary" className="font-mono text-xs">
                {kbItemId}
              </Badge>
            )}
            {typeof kbRiskScore === 'number' && (
              <Badge variant="outline" className="text-xs">
                Risk: {kbRiskScore}
              </Badge>
            )}
          </div>
        )}

        {/* Markdown content */}
        <div className="p-4 prose prose-sm max-w-none dark:prose-invert [&_.prose]:text-sm [&_.prose_p]:my-2 [&_.prose_ul]:my-2 [&_.prose_li]:my-0.5">
          <MarkdownViewer content={content} />
        </div>
      </div>
    </CollapsibleSection>
  )
}
